import React, { useState, useRef } from 'react';
import { uploadMultipleImages } from '../utils/cloudinaryUpload';

interface CarPhotos {
    [key: string]: File | null;
    front: File | null;
    driverSide: File | null;
    rear: File | null;
    passengerSide: File | null;
    odometer: File | null;
    additional: File | null;
}


interface FieldErrors {
    identificationNumber: boolean;
    make: boolean;
    model: boolean;
    year: boolean;
    mileage: boolean;
    exteriorColor: boolean;
    interiorColor: boolean;
    exteriorCondition: boolean;
    interiorCondition: boolean;
    fullName: boolean;
    phoneNumber: boolean;
    email: boolean;
    zipCode: boolean;
    preferredContactMethod: boolean;
}

const CarAppraisal: React.FC = () => {
    const formRef = useRef<HTMLFormElement>(null);
    const [identificationType, setIdentificationType] = useState<'vin' | 'license'>('vin');
    const [carPhotos, setCarPhotos] = useState<CarPhotos>({
        front: null,
        driverSide: null,
        rear: null,
        passengerSide: null,
        odometer: null,
        additional: null
    });
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
        identificationNumber: false,
        make: false,
        model: false,
        year: false,
        mileage: false,
        exteriorColor: false,
        interiorColor: false,
        exteriorCondition: false,
        interiorCondition: false,
        fullName: false,
        phoneNumber: false,
        email: false,
        zipCode: false,
        preferredContactMethod: false
    });
    const [vinValidationError, setVinValidationError] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
    const [isUploading, setIsUploading] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');

    // Refs for scrolling to errors
    const identificationRef = useRef<HTMLInputElement>(null);
    const makeRef = useRef<HTMLInputElement>(null);
    const modelRef = useRef<HTMLInputElement>(null);
    const yearRef = useRef<HTMLInputElement>(null);
    const mileageRef = useRef<HTMLInputElement>(null);
    const exteriorColorRef = useRef<HTMLSelectElement>(null);
    const interiorColorRef = useRef<HTMLSelectElement>(null);
    const exteriorConditionRef = useRef<HTMLSelectElement>(null);
    const interiorConditionRef = useRef<HTMLSelectElement>(null);
    const fullNameRef = useRef<HTMLInputElement>(null);
    const phoneNumberRef = useRef<HTMLInputElement>(null);
    const emailRef = useRef<HTMLInputElement>(null);
    const zipCodeRef = useRef<HTMLInputElement>(null);
    const preferredContactMethodRef = useRef<HTMLSelectElement>(null);
    const frontPhotoRef = useRef<HTMLLabelElement>(null);
    const driverSidePhotoRef = useRef<HTMLLabelElement>(null);
    const rearPhotoRef = useRef<HTMLLabelElement>(null);
    const passengerSidePhotoRef = useRef<HTMLLabelElement>(null);
    const odometerPhotoRef = useRef<HTMLLabelElement>(null);
    const successMessageRef = useRef<HTMLDivElement>(null);

    const handlePhotoChange = (side: keyof CarPhotos, event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Check file size (10MB limit for mobile compatibility)
            if (file.size > 10 * 1024 * 1024) {
                alert(`File size must be less than 10MB. ${file.name} is ${(file.size / 1024 / 1024).toFixed(2)}MB`);
                return;
            }

            // Check file type - be more permissive for mobile
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
            if (!allowedTypes.includes(file.type.toLowerCase()) && !file.type.startsWith('image/')) {
                alert('Please select a valid image file (JPEG, PNG, WebP, HEIC)');
                return;
            }

            console.log('File selected:', {
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified
            });

            setCarPhotos(prev => ({
                ...prev,
                [side]: file
            }));
        }
    };

    const handleIdentificationTypeChange = (type: 'vin' | 'license') => {
        setIdentificationType(type);
        // Clear VIN validation error when switching identification types
        setVinValidationError('');
        // Clear identification number error
        setFieldErrors(prev => ({
            ...prev,
            identificationNumber: false
        }));
    };

    const handleVINChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trim().toUpperCase();

        if (identificationType === 'vin') {
            if (value.length === 0) {
                setVinValidationError('VIN Number is required');
                setFieldErrors(prev => ({
                    ...prev,
                    identificationNumber: true
                }));
            } else if (value.length !== 17 || !/^[A-Z0-9]{17}$/.test(value)) {
                setVinValidationError('VIN should be exactly 17 alphanumeric characters (no spaces or special characters)');
                setFieldErrors(prev => ({
                    ...prev,
                    identificationNumber: true
                }));
            } else {
                // VIN is valid (exactly 17 alphanumeric characters)
                setVinValidationError('');
                setFieldErrors(prev => ({
                    ...prev,
                    identificationNumber: false
                }));
            }
        }
    };

    // Phone number formatting helper
    const formatPhoneNumber = (value: string) => {
        const digits = value.replace(/\D/g, '').slice(0, 10);
        if (digits.length === 0) return '';
        if (digits.length < 4) return `(${digits}`;
        if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    };

    // Phone number change handler
    const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/\D/g, '');
        setPhoneNumber(raw);
        // Only show error if not empty and not valid
        setFieldErrors(prev => ({
            ...prev,
            phoneNumber: raw.length > 0 && raw.length !== 10
        }));
    };

    const scrollToElement = (element: HTMLElement | null) => {
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
            // Focus on the element after scrolling
            setTimeout(() => {
                if (element.tagName === 'LABEL') {
                    const input = element.querySelector('input');
                    if (input) input.focus();
                } else {
                    element.focus();
                }
            }, 500);
        }
    };

    const validateForm = (form: HTMLFormElement) => {
        // VIN validation - must be exactly 17 characters when identificationType is 'vin'
        const vinValue = form.identificationNumber.value.trim().toUpperCase();
        const isVinInvalid = identificationType === 'vin' && (vinValue.length !== 17 || !/^[A-Z0-9]{17}$/.test(vinValue));
        const isIdentificationEmpty = !vinValue;

        // Set VIN validation error message
        if (identificationType === 'vin') {
            if (isIdentificationEmpty) {
                setVinValidationError('VIN Number is required');
            } else if (isVinInvalid) {
                setVinValidationError('VIN should be exactly 17 alphanumeric characters (no spaces or special characters)');
            } else {
                setVinValidationError('');
            }
        } else {
            setVinValidationError('');
        }

        const newFieldErrors: FieldErrors = {
            identificationNumber: isIdentificationEmpty || isVinInvalid,
            make: !form.make.value.trim(),
            model: !form.model.value.trim(),
            year: !form.year.value || parseInt(form.year.value) < 1900 || parseInt(form.year.value) > new Date().getFullYear() + 1,
            mileage: !form.mileage.value || parseInt(form.mileage.value) < 0,
            exteriorColor: !form.exteriorColor.value,
            interiorColor: !form.interiorColor.value,
            exteriorCondition: !form.exteriorCondition.value,
            interiorCondition: !form.interiorCondition.value,
            fullName: !form.fullName.value.trim(),
            phoneNumber: phoneNumber.length !== 10,
            email: !form.email.value.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.value),
            zipCode: !form.zipCode.value.trim(),
            preferredContactMethod: !form.preferredContactMethod.value
        };

        setFieldErrors(newFieldErrors);

        // Find first error and scroll to it
        const errorRefs = [
            { error: newFieldErrors.identificationNumber, ref: identificationRef },
            { error: newFieldErrors.make, ref: makeRef },
            { error: newFieldErrors.model, ref: modelRef },
            { error: newFieldErrors.year, ref: yearRef },
            { error: newFieldErrors.mileage, ref: mileageRef },
            { error: newFieldErrors.exteriorColor, ref: exteriorColorRef },
            { error: newFieldErrors.interiorColor, ref: interiorColorRef },
            { error: newFieldErrors.exteriorCondition, ref: exteriorConditionRef },
            { error: newFieldErrors.interiorCondition, ref: interiorConditionRef },
            { error: newFieldErrors.fullName, ref: fullNameRef },
            { error: newFieldErrors.phoneNumber, ref: phoneNumberRef },
            { error: newFieldErrors.email, ref: emailRef },
            { error: newFieldErrors.zipCode, ref: zipCodeRef },
            { error: newFieldErrors.preferredContactMethod, ref: preferredContactMethodRef }
        ];

        const firstError = errorRefs.find(item => item.error);
        if (firstError && firstError.ref.current) {
            scrollToElement(firstError.ref.current);
        }

        return !Object.values(newFieldErrors).some(error => error) &&
            termsAccepted;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // Validate form
        if (!validateForm(e.currentTarget)) {
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            const identificationNumber = e.currentTarget.identificationNumber.value.trim().toUpperCase();

            // Initialize photoUrls in outer scope for cleanup
            let photoUrls: { [key: string]: string } = {};

            // Prepare form data first (without photos)
            const formDataWithoutPhotos = {
                identificationType,
                identificationNumber,
                make: e.currentTarget.make.value,
                model: e.currentTarget.model.value,
                year: parseInt(e.currentTarget.year.value),
                mileage: parseInt(e.currentTarget.mileage.value),
                exteriorColor: e.currentTarget.exteriorColor.value,
                interiorColor: e.currentTarget.interiorColor.value,
                exteriorCondition: e.currentTarget.exteriorCondition.value,
                interiorCondition: e.currentTarget.interiorCondition.value,
                fullName: e.currentTarget.fullName.value,
                phoneNumber: phoneNumber,
                email: e.currentTarget.email.value,
                zipCode: e.currentTarget.zipCode.value,
                preferredContactMethod: e.currentTarget.preferredContactMethod.value,
                additionalNotes: e.currentTarget.additionalNotes.value,
                submissionDate: new Date().toISOString()
            };

            // Dynamic API URL for local and deployed environments
            const getApiUrl = (endpoint: string) => {
                // Check if we're in development (localhost or local IP)
                if (window.location.hostname === 'localhost' ||
                    window.location.hostname === '127.0.0.1' ||
                    window.location.hostname.startsWith('192.168.') ||
                    window.location.hostname.startsWith('10.') ||
                    window.location.hostname.startsWith('172.')) {
                    // For local development, use the same IP as the frontend for backend
                    const protocol = window.location.protocol;
                    const hostname = window.location.hostname;
                    return `${protocol}//${hostname}:5001/api/${endpoint}`;
                }

                // For production, use the same domain as the frontend
                const protocol = window.location.protocol;
                const hostname = window.location.hostname;
                const port = window.location.port;

                // For same-domain deployment (recommended)
                const baseUrl = port ? `${protocol}//${hostname}:${port}` : `${protocol}//${hostname}`;
                return `${baseUrl}/api/${endpoint}`;
            };

            // Test form validation first (without photos)
            const validateApiUrl = getApiUrl('validate');
            console.log('Testing form validation without photos...');
            console.log('Validation API URL:', validateApiUrl);
            console.log('Form data for validation:', formDataWithoutPhotos);
            console.log('Current location:', window.location.href);
            console.log('User agent:', navigator.userAgent);
            console.log('Hostname:', window.location.hostname);
            console.log('Protocol:', window.location.protocol);
            console.log('Port:', window.location.port);

            // First, test if the API is reachable
            try {
                const healthCheckUrl = getApiUrl('health');
                console.log('Testing API health check at:', healthCheckUrl);

                const healthController = new AbortController();
                const healthTimeout = setTimeout(() => healthController.abort(), 10000); // 10 second timeout

                const healthResponse = await fetch(healthCheckUrl, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    signal: healthController.signal
                });

                clearTimeout(healthTimeout);

                if (!healthResponse.ok) {
                    throw new Error(`API health check failed: ${healthResponse.status} ${healthResponse.statusText}`);
                }

                const healthData = await healthResponse.json();
                console.log('API health check passed:', healthData);
            } catch (healthError) {
                console.error('API health check failed:', healthError);

                if (healthError instanceof Error && healthError.name === 'AbortError') {
                    throw new Error('Server connection timed out. Please check your internet connection and try again.');
                }

                throw new Error('Unable to connect to the server. Please ensure the backend is running and try again.');
            }

            try {
                const validateController = new AbortController();
                const validateTimeout = setTimeout(() => validateController.abort(), 15000); // 15 second timeout

                const validateResponse = await fetch(validateApiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(formDataWithoutPhotos),
                    signal: validateController.signal
                });

                clearTimeout(validateTimeout);

                console.log('Validation response status:', validateResponse.status);
                console.log('Validation response headers:', Object.fromEntries(validateResponse.headers.entries()));

                const validateResponseText = await validateResponse.text();
                console.log('Validation response body:', validateResponseText);

                if (!validateResponse.ok) {
                    let validateData;
                    try {
                        validateData = JSON.parse(validateResponseText);
                    } catch (parseError) {
                        console.error('Failed to parse validation response as JSON:', parseError);
                        throw new Error(`Validation failed: ${validateResponse.status} ${validateResponse.statusText} - ${validateResponseText}`);
                    }
                    throw new Error(validateData.error || 'Form validation failed');
                }
            } catch (validationError) {
                console.error('Validation request failed:', validationError);

                if (validationError instanceof Error && validationError.name === 'AbortError') {
                    throw new Error('Validation request timed out. Please check your internet connection and try again.');
                }

                // Check if it's a network error
                if (validationError instanceof TypeError && (validationError as Error).message.includes('fetch')) {
                    throw new Error('Network error: Unable to reach the server. Please check your internet connection and try again.');
                }

                // Check if it's a CORS error
                if (validationError instanceof Error && (validationError.message.includes('CORS') || validationError.message.includes('cross-origin'))) {
                    throw new Error('Server configuration error. Please contact support.');
                }

                throw validationError;
            }

            console.log('Form validation passed, now uploading images...');

            // Upload images to Cloudinary only after form validation passes

            if (Object.values(carPhotos).some(photo => photo !== null)) {
                try {
                    const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'car_appraisals';
                    console.log('Starting image uploads to Cloudinary...');
                    console.log('Environment variables:', {
                        cloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME,
                        uploadPreset: uploadPreset
                    });
                    setIsUploading(true);
                    setUploadProgress({});

                    // Create filename prefix based on identification type
                    const filenamePrefix = identificationType === 'vin'
                        ? identificationNumber
                        : `LICENSE_${identificationNumber}`;

                    console.log('Generated filename prefix:', filenamePrefix);
                    console.log('Identification number:', identificationNumber);
                    console.log('Identification type:', identificationType);

                    photoUrls = await uploadMultipleImages(
                        carPhotos,
                        uploadPreset,
                        filenamePrefix, // Use VIN or LICENSE prefix
                        (key, progress) => {
                            setUploadProgress(prev => ({
                                ...prev,
                                [key]: progress
                            }));
                        }
                    );

                    console.log('Images uploaded successfully:', photoUrls);
                    console.log('Files uploaded with prefix:', filenamePrefix);
                    console.log('Identification type:', identificationType);
                    setIsUploading(false);
                } catch (uploadError) {
                    console.error('Error uploading images:', uploadError);
                    setIsUploading(false);
                    setUploadProgress({});

                    let errorMessage = 'Upload failed';
                    if (uploadError instanceof Error) {
                        errorMessage = uploadError.message;

                        // Provide more user-friendly error messages
                        if (errorMessage.includes('timeout')) {
                            errorMessage = 'Upload timed out. Please check your internet connection and try again.';
                        } else if (errorMessage.includes('Network error')) {
                            errorMessage = 'Network connection issue. Please check your internet and try again.';
                        } else if (errorMessage.includes('File too large')) {
                            errorMessage = 'Image file is too large. Please choose a smaller image or try again.';
                        } else if (errorMessage.includes('Invalid file format')) {
                            errorMessage = 'Please use JPEG, PNG, or WebP image formats.';
                        }
                    }

                    throw new Error(`Failed to upload images: ${errorMessage}`);
                }
            }

            const formData = {
                ...formDataWithoutPhotos,
                photos: photoUrls // Include the uploaded image URLs
            };

            const apiUrl = getApiUrl('submit');
            console.log('Submitting form data to API URL:', apiUrl);
            console.log('Form data being sent:', formData);
            console.log('Form data JSON:', JSON.stringify(formData, null, 2));
            console.log('Form data keys:', Object.keys(formData));
            console.log('Make field value:', formData.make);
            console.log('Model field value:', formData.model);

            // Submit form data with image URLs to backend
            console.log('Submitting form data to API URL:', apiUrl);
            console.log('Form data being sent:', formData);

            try {
                const submitController = new AbortController();
                const submitTimeout = setTimeout(() => submitController.abort(), 30000); // 30 second timeout

                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(formData),
                    signal: submitController.signal
                });

                clearTimeout(submitTimeout);

                console.log('Response status:', response.status);
                console.log('Response headers:', Object.fromEntries(response.headers.entries()));

                const responseText = await response.text();
                console.log('Response body:', responseText);

                let data;
                try {
                    data = JSON.parse(responseText);
                } catch (parseError) {
                    console.error('Failed to parse response as JSON:', parseError);
                    throw new Error(`Server returned invalid JSON: ${responseText}`);
                }

                if (!response.ok || !data.success) {
                    throw new Error(data.error || 'Failed to submit form');
                }
            } catch (submitError) {
                console.error('Form submission failed:', submitError);

                if (submitError instanceof Error && submitError.name === 'AbortError') {
                    throw new Error('Form submission timed out. Please check your internet connection and try again.');
                }

                // Check if it's a network error
                if (submitError instanceof TypeError && (submitError as Error).message.includes('fetch')) {
                    throw new Error('Network error: Unable to reach the server. Please check your internet connection and try again.');
                }

                // Check if it's a CORS error
                if (submitError instanceof Error && (submitError.message.includes('CORS') || submitError.message.includes('cross-origin'))) {
                    throw new Error('Server configuration error. Please contact support.');
                }

                throw submitError;
            }

            setSubmitStatus('success');
            // Reset form using the ref
            if (formRef.current) {
                formRef.current.reset();
            }
            setCarPhotos({
                front: null,
                driverSide: null,
                rear: null,
                passengerSide: null,
                odometer: null,
                additional: null
            });
            setUploadProgress({});
            setIsUploading(false);
            // Clear all errors
            setFieldErrors({
                identificationNumber: false,
                make: false,
                model: false,
                year: false,
                mileage: false,
                exteriorColor: false,
                interiorColor: false,
                exteriorCondition: false,
                interiorCondition: false,
                fullName: false,
                phoneNumber: false,
                email: false,
                zipCode: false,
                preferredContactMethod: false
            });
            setVinValidationError('');
            // Reset terms acceptance
            setTermsAccepted(false);
            // Reset phone number
            setPhoneNumber('');

            // Scroll to success message
            setTimeout(() => {
                if (successMessageRef.current) {
                    successMessageRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }, 100);
        } catch (error) {
            console.error('Error submitting form:', error);

            setSubmitStatus('error');
            // Show user-friendly error message
            const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
            alert(`Submission failed: ${errorMessage}. Please try again.`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="font-sans bg-gray-50">
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="max-w-3xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl font-bold text-gray-800 mb-4">Get Your Car Appraised</h2>
                            <p className="text-gray-600">Fill out the form below to get a free, no-obligation appraisal of your vehicle.</p>
                        </div>

                        {submitStatus === 'success' && (
                            <div ref={successMessageRef} className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                                Thank you for your submission! We'll review your information and get back to you soon.
                            </div>
                        )}

                        {submitStatus === 'error' && (
                            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                                There was an error submitting your form. Please try again.
                                <button
                                    onClick={() => {
                                        const getTestApiUrl = (endpoint: string) => {
                                            if (window.location.hostname === 'localhost' ||
                                                window.location.hostname === '127.0.0.1' ||
                                                window.location.hostname.startsWith('192.168.') ||
                                                window.location.hostname.startsWith('10.') ||
                                                window.location.hostname.startsWith('172.')) {
                                                const protocol = window.location.protocol;
                                                const hostname = window.location.hostname;
                                                return `${protocol}//${hostname}:5001/api/${endpoint}`;
                                            }
                                            return `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}/api/${endpoint}`;
                                        };

                                        const apiUrl = getTestApiUrl('validate');
                                        console.log('Testing API connection to:', apiUrl);
                                        fetch(apiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ test: true }) })
                                            .then(res => console.log('Test response:', res.status, res.statusText))
                                            .catch(err => console.error('Test error:', err));
                                    }}
                                    className="ml-4 px-3 py-1 bg-red-600 text-white rounded text-sm"
                                >
                                    Test Connection
                                </button>
                            </div>
                        )}

                        {/* Upload Information */}
                        {Object.values(carPhotos).some(photo => photo !== null) && (
                            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center space-x-2">
                                    <i className="fas fa-info-circle text-blue-600"></i>
                                    <span className="text-blue-800">
                                        <strong>Note:</strong> When you click Submit, your form will be validated first, then your photos will be uploaded to our secure cloud storage with {identificationType === 'vin' ? 'VIN' : 'License Plate'} naming format.
                                        {/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && (
                                            <span className="block mt-2 text-sm">
                                                📱 Mobile detected: Large photos will be automatically compressed for faster upload.
                                            </span>
                                        )}
                                    </span>
                                </div>
                            </div>
                        )}

                        <form ref={formRef} onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-md">
                            {/* Vehicle Identification */}
                            <div className="mb-8">
                                <h3 className="text-xl font-bold text-gray-800 mb-4">Vehicle Identification</h3>
                                <div className="mb-4">
                                    <div className="flex space-x-6 mb-4">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="identificationType"
                                                value="vin"
                                                checked={identificationType === 'vin'}
                                                onChange={() => handleIdentificationTypeChange('vin')}
                                                className="mr-2"
                                            />
                                            <span className="text-gray-700">VIN Number</span>
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="identificationType"
                                                value="license"
                                                checked={identificationType === 'license'}
                                                onChange={() => handleIdentificationTypeChange('license')}
                                                className="mr-2"
                                            />
                                            <span className="text-gray-700">License Plate</span>
                                        </label>
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">
                                            {identificationType === 'vin' ? 'VIN Number' : 'License Plate'} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            ref={identificationRef}
                                            type="text"
                                            name="identificationNumber"
                                            placeholder={identificationType === 'vin' ? "Enter VIN Number" : "Enter License Plate"}
                                            maxLength={identificationType === 'vin' ? 17 : undefined}
                                            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${fieldErrors.identificationNumber ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                            required
                                            onChange={handleVINChange}
                                        />
                                        {fieldErrors.identificationNumber && (
                                            <p className="text-red-500 text-sm mt-1">
                                                {identificationType === 'vin'
                                                    ? vinValidationError
                                                    : 'License Plate is required'
                                                }
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Vehicle Information */}
                            <div className="mb-8">
                                <h3 className="text-xl font-bold text-gray-800 mb-4">Vehicle Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Make <span className="text-red-500">*</span></label>
                                        <input
                                            ref={makeRef}
                                            type="text"
                                            name="make"
                                            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${fieldErrors.make ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                            required
                                        />
                                        {fieldErrors.make && (
                                            <p className="text-red-500 text-sm mt-1">Make is required</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Model <span className="text-red-500">*</span></label>
                                        <input
                                            ref={modelRef}
                                            type="text"
                                            name="model"
                                            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${fieldErrors.model ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                            required
                                        />
                                        {fieldErrors.model && (
                                            <p className="text-red-500 text-sm mt-1">Model is required</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Year <span className="text-red-500">*</span></label>
                                        <input
                                            ref={yearRef}
                                            type="number"
                                            name="year"
                                            min="1900"
                                            max={new Date().getFullYear() + 1}
                                            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${fieldErrors.year ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                            required
                                        />
                                        {fieldErrors.year && (
                                            <p className="text-red-500 text-sm mt-1">Valid year is required (1900-{new Date().getFullYear() + 1})</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Mileage <span className="text-red-500">*</span></label>
                                        <input
                                            ref={mileageRef}
                                            type="number"
                                            name="mileage"
                                            min="0"
                                            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${fieldErrors.mileage ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                            required
                                        />
                                        {fieldErrors.mileage && (
                                            <p className="text-red-500 text-sm mt-1">Valid mileage is required</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Exterior Color <span className="text-red-500">*</span></label>
                                        <select
                                            ref={exteriorColorRef}
                                            name="exteriorColor"
                                            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${fieldErrors.exteriorColor ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                            required
                                        >
                                            <option value="">Select color</option>
                                            <option value="black">Black</option>
                                            <option value="white">White</option>
                                            <option value="silver">Silver</option>
                                            <option value="gray">Gray</option>
                                            <option value="red">Red</option>
                                            <option value="blue">Blue</option>
                                            <option value="green">Green</option>
                                            <option value="brown">Brown</option>
                                            <option value="beige">Beige</option>
                                            <option value="gold">Gold</option>
                                            <option value="other">Other</option>
                                        </select>
                                        {fieldErrors.exteriorColor && (
                                            <p className="text-red-500 text-sm mt-1">Exterior color is required</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Interior Color <span className="text-red-500">*</span></label>
                                        <select
                                            ref={interiorColorRef}
                                            name="interiorColor"
                                            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${fieldErrors.interiorColor ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                            required
                                        >
                                            <option value="">Select color</option>
                                            <option value="black">Black</option>
                                            <option value="gray">Gray</option>
                                            <option value="beige">Beige</option>
                                            <option value="tan">Tan</option>
                                            <option value="brown">Brown</option>
                                            <option value="red">Red</option>
                                            <option value="blue">Blue</option>
                                            <option value="white">White</option>
                                            <option value="cream">Cream</option>
                                            <option value="other">Other</option>
                                        </select>
                                        {fieldErrors.interiorColor && (
                                            <p className="text-red-500 text-sm mt-1">Interior color is required</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Condition */}
                            <div className="mb-8">
                                <h3 className="text-xl font-bold text-gray-800 mb-4">Vehicle Condition</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Exterior Condition <span className="text-red-500">*</span></label>
                                        <select
                                            ref={exteriorConditionRef}
                                            name="exteriorCondition"
                                            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${fieldErrors.exteriorCondition ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                            required
                                        >
                                            <option value="">Select condition</option>
                                            <option value="excellent">Excellent</option>
                                            <option value="good">Good</option>
                                            <option value="fair">Fair</option>
                                            <option value="poor">Poor</option>
                                        </select>
                                        {fieldErrors.exteriorCondition && (
                                            <p className="text-red-500 text-sm mt-1">Exterior condition is required</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Interior Condition <span className="text-red-500">*</span></label>
                                        <select
                                            ref={interiorConditionRef}
                                            name="interiorCondition"
                                            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${fieldErrors.interiorCondition ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                            required
                                        >
                                            <option value="">Select condition</option>
                                            <option value="excellent">Excellent</option>
                                            <option value="good">Good</option>
                                            <option value="fair">Fair</option>
                                            <option value="poor">Poor</option>
                                        </select>
                                        {fieldErrors.interiorCondition && (
                                            <p className="text-red-500 text-sm mt-1">Interior condition is required</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Vehicle Photos */}
                            <div className="mb-8">
                                <h3 className="text-xl font-bold text-gray-800 mb-4">Vehicle Photos</h3>
                                <p className="text-gray-600 mb-4">Please upload clear photos of your vehicle from all four sides (optional)</p>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Front Photo */}
                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                                        <label ref={frontPhotoRef} className="block cursor-pointer min-h-[120px] flex flex-col items-center justify-center">
                                            <div className="mb-2">
                                                <i className="fas fa-car text-3xl text-gray-400"></i>
                                            </div>
                                            <span className="block text-gray-700 font-medium mb-2">Front View</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handlePhotoChange('front', e)}
                                                className="hidden"
                                            />
                                            <span className="text-sm text-gray-500">
                                                {carPhotos.front ? (
                                                    <div className="flex items-center space-x-2">
                                                        <span>{carPhotos.front.name}</span>
                                                        {uploadProgress.front !== undefined && (
                                                            <div className="flex items-center space-x-1">
                                                                <div className="w-3 h-3 bg-blue-600 rounded-full animate-pulse"></div>
                                                                <span className="text-xs text-blue-600">{uploadProgress.front}%</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : 'Tap to upload (optional)'}
                                            </span>
                                        </label>
                                    </div>

                                    {/* Driver Side Photo */}
                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                                        <label ref={driverSidePhotoRef} className="block cursor-pointer min-h-[120px] flex flex-col items-center justify-center">
                                            <div className="mb-2">
                                                <i className="fas fa-car-side text-3xl text-gray-400"></i>
                                            </div>
                                            <span className="block text-gray-700 font-medium mb-2">Driver Side</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handlePhotoChange('driverSide', e)}
                                                className="hidden"
                                            />
                                            <span className="text-sm text-gray-500">
                                                {carPhotos.driverSide ? carPhotos.driverSide.name : 'Tap to upload (optional)'}
                                            </span>
                                        </label>
                                    </div>

                                    {/* Rear Photo */}
                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                                        <label ref={rearPhotoRef} className="block cursor-pointer min-h-[120px] flex flex-col items-center justify-center">
                                            <div className="mb-2">
                                                <i className="fas fa-car-rear text-3xl text-gray-400 transform"></i>
                                            </div>
                                            <span className="block text-gray-700 font-medium mb-2">Rear View</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handlePhotoChange('rear', e)}
                                                className="hidden"
                                            />
                                            <span className="text-sm text-gray-500">
                                                {carPhotos.rear ? carPhotos.rear.name : 'Tap to upload (optional)'}
                                            </span>
                                        </label>
                                    </div>

                                    {/* Passenger Side Photo */}
                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                                        <label ref={passengerSidePhotoRef} className="block cursor-pointer min-h-[120px] flex flex-col items-center justify-center">
                                            <div className="mb-2">
                                                <i className="fas fa-car-side text-3xl text-gray-400 transform scale-x-[-1]"></i>
                                            </div>
                                            <span className="block text-gray-700 font-medium mb-2">Passenger Side</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handlePhotoChange('passengerSide', e)}
                                                className="hidden"
                                            />
                                            <span className="text-sm text-gray-500">
                                                {carPhotos.passengerSide ? carPhotos.passengerSide.name : 'Tap to upload (optional)'}
                                            </span>
                                        </label>
                                    </div>

                                    {/* Odometer Photo */}
                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center md:col-span-2">
                                        <label ref={odometerPhotoRef} className="block cursor-pointer min-h-[120px] flex flex-col items-center justify-center">
                                            <div className="mb-2">
                                                <i className="fas fa-tachometer-alt text-3xl text-gray-400"></i>
                                            </div>
                                            <span className="block text-gray-700 font-medium mb-2">Odometer Photo</span>
                                            <p className="text-sm text-gray-500 mb-2">Please upload a clear photo of your vehicle's odometer showing the current mileage</p>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handlePhotoChange('odometer', e)}
                                                className="hidden"
                                            />
                                            <span className="text-sm text-gray-500">
                                                {carPhotos.odometer ? carPhotos.odometer.name : 'Tap to upload (optional)'}
                                            </span>
                                        </label>
                                    </div>

                                    {/* Additional Photo */}
                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center md:col-span-2">
                                        <label className="block cursor-pointer min-h-[120px] flex flex-col items-center justify-center">
                                            <div className="mb-2">
                                                <i className="fas fa-images text-3xl text-gray-400"></i>
                                            </div>
                                            <span className="block text-gray-700 font-medium mb-2">Additional Photos</span>
                                            <p className="text-sm text-gray-500 mb-2">Upload any additional photos of damage, interior, or other details</p>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handlePhotoChange('additional', e)}
                                                className="hidden"
                                            />
                                            <span className="text-sm text-gray-500">
                                                {carPhotos.additional ? carPhotos.additional.name : 'Tap to upload (optional)'}
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Contact Information */}
                            <div className="mb-8">
                                <h3 className="text-xl font-bold text-gray-800 mb-4">Contact Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Full Name <span className="text-red-500">*</span></label>
                                        <input
                                            ref={fullNameRef}
                                            type="text"
                                            name="fullName"
                                            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${fieldErrors.fullName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                            required
                                        />
                                        {fieldErrors.fullName && (
                                            <p className="text-red-500 text-sm mt-1">Full name is required</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Phone Number <span className="text-red-500">*</span></label>
                                        <input
                                            ref={phoneNumberRef}
                                            type="tel"
                                            name="phoneNumber"
                                            placeholder="(555) 123-4567"
                                            maxLength={14}
                                            value={formatPhoneNumber(phoneNumber)}
                                            onChange={handlePhoneNumberChange}
                                            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${fieldErrors.phoneNumber ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                            required
                                        />
                                        {fieldErrors.phoneNumber && phoneNumber.length > 0 && (
                                            <p className="text-red-500 text-sm mt-1">Valid phone number is required (10 digits)</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Email <span className="text-red-500">*</span></label>
                                        <input
                                            ref={emailRef}
                                            type="email"
                                            name="email"
                                            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${fieldErrors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                            required
                                        />
                                        {fieldErrors.email && (
                                            <p className="text-red-500 text-sm mt-1">Valid email address is required</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Zip Code <span className="text-red-500">*</span></label>
                                        <input
                                            ref={zipCodeRef}
                                            type="text"
                                            name="zipCode"
                                            placeholder="Enter zip code"
                                            maxLength={10}
                                            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${fieldErrors.zipCode ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                            required
                                        />
                                        {fieldErrors.zipCode && (
                                            <p className="text-red-500 text-sm mt-1">Zip code is required</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Preferred Contact Method <span className="text-red-500">*</span></label>
                                        <select
                                            ref={preferredContactMethodRef}
                                            name="preferredContactMethod"
                                            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${fieldErrors.preferredContactMethod ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                            required
                                        >
                                            <option value="">Select method</option>
                                            <option value="phone">Phone</option>
                                            <option value="email">Email</option>
                                            <option value="text">Text</option>
                                        </select>
                                        {fieldErrors.preferredContactMethod && (
                                            <p className="text-red-500 text-sm mt-1">Preferred contact method is required</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Additional Notes */}
                            <div className="mb-8">
                                <label className="block text-gray-700 font-medium mb-2">Additional Notes</label>
                                <textarea name="additionalNotes" rows={4} className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500" placeholder="Please provide any additional information about your vehicle..." />
                            </div>

                            {/* Terms and Conditions */}
                            <div className="mb-8">
                                <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <input
                                        type="checkbox"
                                        id="termsAccepted"
                                        checked={termsAccepted}
                                        onChange={(e) => setTermsAccepted(e.target.checked)}
                                        className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                                        required
                                    />
                                    <label htmlFor="termsAccepted" className="text-sm text-gray-700 leading-relaxed">
                                        <span className="text-red-500 font-medium">*</span> By submitting this form, you confirm that all details provided are true and accurate to the best of your knowledge. Incomplete or incorrect information may affect your appraisal value.
                                    </label>
                                </div>
                            </div>

                            {/* Upload Progress Indicator */}
                            {isUploading && (
                                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-blue-800 font-medium">Uploading Images...</span>
                                        <span className="text-blue-600 text-sm">
                                            {Object.keys(uploadProgress).length} of {Object.values(carPhotos).filter(photo => photo !== null).length} complete
                                        </span>
                                    </div>
                                    <div className="space-y-2">
                                        {Object.entries(carPhotos).map(([key, file]) => {
                                            if (!file) return null;
                                            const progress = uploadProgress[key] || 0;
                                            const photoNames: { [key: string]: string } = {
                                                front: 'Front View',
                                                driverSide: 'Driver Side',
                                                rear: 'Rear View',
                                                passengerSide: 'Passenger Side',
                                                odometer: 'Odometer',
                                                additional: 'Additional'
                                            };
                                            return (
                                                <div key={key} className="flex items-center space-x-2">
                                                    <span className="text-sm text-gray-600 w-24">{photoNames[key]}:</span>
                                                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                                                        <div
                                                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                                            style={{ width: `${progress}%` }}
                                                        ></div>
                                                    </div>
                                                    <span className="text-sm text-gray-500 w-8">{progress}%</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <button
                                type="submit"
                                className={`w-full px-6 py-3 rounded-md font-medium transition ${isSubmitting || isUploading || !termsAccepted
                                        ? 'bg-gray-400 cursor-not-allowed opacity-50'
                                        : 'bg-red-600 hover:bg-red-700 text-white'
                                    }`}
                                disabled={isSubmitting || isUploading || !termsAccepted}
                            >
                                {isUploading ? 'Uploading Images...' : isSubmitting ? 'Submitting...' : 'Submit Appraisal Request'}
                            </button>
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CarAppraisal;